import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiFilm, FiMusic, FiUpload, FiDownload, FiRefreshCw, FiLoader, FiVideo } from 'react-icons/fi';
import { BsFileMusic } from 'react-icons/bs';
import SEO from '../components/SEO';
import { videoToAudioSchemas } from '../schemas';
import API_BASE_URL from '../config';
import './VideoToAudio.css';

const VideoToAudio = () => {
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isDirectExtracting, setIsDirectExtracting] = useState(false);
  const [directExtractProgress, setDirectExtractProgress] = useState(0);
  const [directAudioUrl, setDirectAudioUrl] = useState(null);
  const [directAudioName, setDirectAudioName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    return () => {
      if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    };
  }, [videoPreviewUrl]);

  useEffect(() => {
    return () => {
      if (directAudioUrl) URL.revokeObjectURL(directAudioUrl);
    };
  }, [directAudioUrl]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndSetVideo(file);
    }
  };

  const validateAndSetVideo = (file) => {
    if (!file.type.startsWith('video/')) {
      setError('Please upload a valid video file');
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      setError('Video file size should be less than 100MB');
      return;
    }

    setError('');
    const url = URL.createObjectURL(file);
    setVideoPreviewUrl(url);
    setVideoFile(file);

    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      setVideoDuration(video.duration);
    };
    video.src = url;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndSetVideo(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const extractAudioDirectMp4 = async () => {
    if (!videoFile) return;

    setIsDirectExtracting(true);
    setDirectExtractProgress(0);
    setDirectAudioName('');
    if (directAudioUrl) URL.revokeObjectURL(directAudioUrl);
    setDirectAudioUrl(null);

    try {
      const formData = new FormData();
      formData.append('video', videoFile);

      const progressInterval = setInterval(() => {
        setDirectExtractProgress(prev => Math.min(prev + 5, 90));
      }, 200);

      const response = await fetch(`${API_BASE_URL}/api/video-to-mp4-audio`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Conversion failed');
      }

      const data = await response.json();
      const blob = new Blob([Uint8Array.from(atob(data.mp4), c => c.charCodeAt(0))], { type: 'audio/mp4' });

      setDirectExtractProgress(100);
      setDirectAudioUrl(URL.createObjectURL(blob));
      setDirectAudioName(data.filename);

    } catch (err) {
      console.error('Direct MP4 extraction error:', err);
      setError(err.message || 'Failed to extract MP4 audio. Please try again.');
    } finally {
      setIsDirectExtracting(false);
    }
  };

  const downloadDirectMp4 = () => {
    if (!directAudioUrl) return;
    const link = document.createElement('a');
    link.href = directAudioUrl;
    const videoName = videoFile ? videoFile.name.replace(/\.[^/.]+$/, '') : 'audio';
    link.download = `${videoName}-audio.mp4`;
    link.click();
  };

  const resetConverter = () => {
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    if (directAudioUrl) URL.revokeObjectURL(directAudioUrl);
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setDirectAudioUrl(null);
    setDirectAudioName('');
    setError('');
    setDirectExtractProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="video-to-audio-page">
      <SEO 
        title="Video to Audio Converter – Extract MP3 Free Online"
        description="Extract audio from any video file online free. Convert video to MP3 or WAV instantly — no software or signup required."
        keywords="video to audio, video to mp3 converter, extract audio from video"
        path="/video-to-audio"
        schema={videoToAudioSchemas}
      />
      <section className="tool-hero">
        <div className="tool-hero-container">
          <motion.div
            className="tool-icon-large"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <BsFileMusic />
          </motion.div>

          <motion.h1
            className="tool-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Video to Audio Extractor
          </motion.h1>

          <motion.p
            className="tool-description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Extract high-quality MP3 audio from your videos. Upload videos up to 100MB.
          </motion.p>
        </div>
      </section>

      <section className="tool-workspace">
        <div className="workspace-container">
          <AnimatePresence mode="wait">
            {!videoFile ? (
              <motion.div
                key="upload"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="upload-zone"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
              >
                <motion.div
                  className="upload-icon"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <FiUpload />
                </motion.div>
                <h3>Upload Your Video</h3>
                <p>Drag and drop or click to browse (up to 100MB)</p>
                <p className="supported-formats">Supported: MP4, MOV, AVI, WebM, MKV</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </motion.div>
            ) : (
              <motion.div
                key="converter"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="converter-workspace"
              >
                <div className="video-audio-grid">
                  <motion.div
                    className="preview-card-minimal"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="card-header-small">
                      <FiFilm />
                      <span>Original Video</span>
                    </div>
                    <div className="card-content">
                      <video
                        key={videoPreviewUrl}
                        ref={videoRef}
                        src={videoPreviewUrl}
                        controls
                        playsInline
                        preload="auto"
                        className="video-preview"
                        onLoadedData={() => console.log('Video ready to play')}
                        onError={(e) => {
                          console.error('Video load error:', e);
                          setError('Failed to load video. Please try another file.');
                        }}
                      />
                    </div>
                    <div className="video-info-bar">
                      <span className="video-info-name">{videoFile ? videoFile.name : ''}</span>
                      <span className="video-info-size">{videoFile ? formatFileSize(videoFile.size) : ''}</span>
                    </div>
                  </motion.div>

                  <motion.div
                    className="preview-card-minimal"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <div className="card-header-small">
                      <FiMusic />
                      <span>Audio Output</span>
                    </div>
                    <div className="card-content">
                      <AnimatePresence mode="wait">
                        {isDirectExtracting ? (
                          <motion.div
                            key="direct-extracting"
                            className="loading-circle"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            <FiLoader className="spinning" />
                            <span>Converting... {directExtractProgress}%</span>
                          </motion.div>
                        ) : directAudioUrl ? (
                          <motion.div
                            key="direct-audio"
                            className="audio-result-minimal"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                          >
                            <FiVideo className="audio-icon-success" />
                            <audio src={directAudioUrl} controls className="audio-player-minimal" />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="waiting"
                            className="waiting-minimal"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                          >
                            <FiMusic />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                </div>

                {error && (
                  <motion.div
                    className="error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {error}
                  </motion.div>
                )}

                <motion.div
                  className="action-buttons-row"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  {!directAudioUrl ? (
                    <motion.button
                      className="btn-primary-large"
                      onClick={extractAudioDirectMp4}
                      disabled={isDirectExtracting}
                      whileHover={{ scale: isDirectExtracting ? 1 : 1.05 }}
                      whileTap={{ scale: isDirectExtracting ? 1 : 0.95 }}
                    >
                      {isDirectExtracting ? (
                        <>
                          <FiLoader className="spinning" />
                          Converting... {directExtractProgress}%
                        </>
                      ) : (
                        <>
                          <FiVideo />
                          Extract Audio (MP4)
                        </>
                      )}
                    </motion.button>
                  ) : (
                    <>
                      <motion.button
                        className="btn-primary-large"
                        onClick={downloadDirectMp4}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring' }}
                      >
                        <FiDownload />
                        Download Audio (MP4)
                      </motion.button>
                      <motion.button
                        className="btn-secondary"
                        onClick={resetConverter}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FiRefreshCw />
                        Start Over
                      </motion.button>
                    </>
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <section className="info-section">
        <div className="info-container">
          <motion.div
            className="info-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3>Why Extract Audio?</h3>
            <ul>
              <li>Create podcasts from video content</li>
              <li>Save music from music videos</li>
              <li>Extract voiceovers and narrations</li>
              <li>Convert interviews to audio format</li>
              <li>Perfect for offline listening</li>
            </ul>
          </motion.div>

          <motion.div
            className="info-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h3>MP4 Audio</h3>
            <ul>
              <li>Universal AAC codec support</li>
              <li>Direct extraction from video - no intermediate format</li>
              <li>Compatible with all devices and players</li>
              <li>High quality at 192kbps bitrate</li>
              <li>Fast server-side conversion</li>
            </ul>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default VideoToAudio;
